import type { AuthErrorSchema, RegistrationErrors, ResetPasswordErrors, VerificationErrors } from "@fola/schemas/auth"
import type { LiteralKeys } from "@fola/utils/types"
import { REGISTRATION_CONSTRAINTS as R } from "@fola/schemas/auth"

const error: LiteralKeys<AuthErrorSchema["error"]> = {
	emailNotVerified: "Your email address is not verified yet.",
	invalidCredentials: "The credentials you entered are invalid.",
	notLoggedIn: "You are not logged in.",
}

const registrationErrors: LiteralKeys<RegistrationErrors> = {
	passwordTooCommon: "The password you entered is on the list of most commonly used passwords.",
	passwordMismatch: "The password and the repeated password don't match.",
	userOrEmailAlreadyExists: "A user with the same credentials already exists.",
	emailNotSent:
		"An error occurred while sending the verification email. This is likely a problem on our side. Please contact us at fola@studiumdigitale.uni-frankfurt.de",
	currentlyDisabled: "Registration is currently unavailable.",
}

const verificationErrors: LiteralKeys<VerificationErrors> = {
	tokenTimeout: "The verification link has expired.",
	invalidToken: "The verification link is invalid.",
}

const resetPasswordErrors: LiteralKeys<ResetPasswordErrors> = {
	emailNotSent:
		"An error occurred while sending the password reset email. Please contact us at fola@studiumdigitale.uni-frankfurt.de",
	userNotFound: "If we have an account with this email address, we have sent you an email with further instructions.",
	tokenTimeout: "The password reset link has expired.",
	invalidToken: "The password reset link is invalid.",
	passwordMismatch: "The password and the repeated password don't match.",
}

const strings = {
	error,
	registrationErrors,
	verificationErrors,
	resetPasswordErrors,
	logintitle: "Log in to FoLA | digital",
	registertitle: "Register for FoLA | digital",
	usernameOrEmail: "Username or email",
	username: "Username",
	displayName: "Display name",
	email: "Email",
	password: "Password",
	repeatPassword: "Repeat Password",
	login: "Log in",
	logout: "Log out",
	register: "Register",
	captchaError:
		"There was an error with the captcha. Please reload the page and try again. if the problem persists, please contact us via fola@studiumdigitale.uni-frankfurt.de.",
	registerAccount: "You don't have an account yet? Register here.",
	loginAccount: "You already have an account? Log in here.",
	resetAccount: "Forgot your password? Reset it here.",
	rememberAccount: "You remember your password? Log in here.",
	userSettings: "My preferences",
	emailVerificationTitle: "Email verification",
	emailVerificationSent:
		"A verification email has been sent to your email address. Please check your inbox and click the link in the email to verify your email address. If you can't find our email, please check your spam folder. If you did not receive an email, please contact us via fola@studiumdigitale.uni-frankfurt.de",
	verificationSuccess: "Your email address has been verified. You can now log in to FoLA | digital .",
	acceptedDatapolicy: "I accept the data privacy policy. (required)",
	acceptedTermsOfUse: "I accept the terms of use. (required)",
	usernamePseudonym: "Please choose a username that is not your real name. Use a pseudonym instead.",
	usernameValidation: `Username must be between ${R.USERNAME_MIN_LENGTH} and ${R.USERNAME_MAX_LENGTH} characters long and must not contain whitespace. Only letters, numbers and underscores are allowed.`,
	passwordValidation: `Password must be at least ${R.PASSWORD_MIN_LENGTH} characters long.`,
	displayNameInfo:
		"This is the name that will be displayed to you and other users. It can be your real name or a pseudonym. You can change this name at any time.",
	displaynameValidation: `Display name must be between ${R.DISPLAY_NAME_MIN_LENGTH} and ${R.DISPLAY_NAME_MAX_LENGTH} characters long.`,
	emailValidation: "Please enter a valid email address.",
	repeatPasswordValidation: "Your password and the repeated password don't match.",
	validPassword: "Please enter a valid password first.",
	notEverythingValid: "Please fill out all required fields.",
	verificationPending:
		"Waiting for your email address to be verified…\n\n Please check your inbox and click the link in the email to verify your email address. If you can't find our email, please check your spam folder. \n\n If you did not receive an email, please contact us via fola@studiumdigitale.uni-frankfurt.de",
	verificationTokenMissing: "The verification link is invalid.",
	resetPassword: "Reset",
	sendResetEmail: "Send password reset email",
	resetEmailTitle: "Reset password via Email",
	resetEmailSent: "If we have an account with this email address, we have sent you an email with further instructions.",
	resetAccountDescription:
		"Enter your username or email address and we will send you a link to reset your password.  Please check your inbox and click the link in the email to reset your password.",
	resetPasswordByEmailInstructions:
		"Thanks for using FoLA | digital! Please reset your password by clicking the following link:\n\n",
	resetPasswordSuccess: "Your password has been reset. You can now log in to FoLA | digital.",
	resetPasswordViewInstructions:
		"Great, you're almost there! Please provide a new password for your account and confirm it, in order to login.",
	resetPasswordPending: "Waiting for your password to be reset…",
	verifyEmail: "Verify Email",
	verifyResetPasswort: "Verify Reset Password",
}

export default strings
export type AuthL10nStrings = typeof strings
